/**
 * Canvas responsive breakpoint tokens.
 *
 * These are the fixed breakpoint tokens from the frozen MyVault Canvas
 * product/UX specification, section 7.2 ("Desired -- a deterministic responsive
 * model"). They are transcribed verbatim from that table so the builder preview,
 * runtime, and public viewer all resolve the same breakpoint for a given
 * viewport width:
 *
 *   | Breakpoint | Viewport width | Effective columns C            |
 *   | desktop    | >= 1200px      | the canvas grid_columns (def 12)|
 *   | tablet     | 768px-1199px   | 8                               |
 *   | phone      | < 768px        | 1                               |
 *
 * `desktop` uses the canvas's own authored `grid_columns` as its effective
 * column count (coordinates are used as authored); `tablet` and `phone` have
 * fixed effective column counts of 8 and 1.
 */

export type BreakpointKind = "desktop" | "tablet" | "phone";

/** Minimum viewport width (px) for the `desktop` breakpoint (spec 7.2). */
export const DESKTOP_MIN_WIDTH = 1200;

/** Minimum viewport width (px) for the `tablet` breakpoint (spec 7.2). */
export const TABLET_MIN_WIDTH = 768;

/**
 * Fixed effective column count `C` for the `tablet` breakpoint (spec 7.2).
 * `desktop` uses the canvas `grid_columns`; `phone` is always 1.
 */
export const TABLET_COLUMNS = 8;

/** Fixed effective column count `C` for the `phone` breakpoint (spec 7.2). */
export const PHONE_COLUMNS = 1;

/**
 * Resolve the breakpoint kind for a viewport width, per the fixed spec 7.2
 * thresholds. `>= 1200` desktop; `768..1199` tablet; `< 768` phone.
 */
export function resolveBreakpointKind(viewportWidth: number): BreakpointKind {
  if (viewportWidth >= DESKTOP_MIN_WIDTH) {
    return "desktop";
  }
  if (viewportWidth >= TABLET_MIN_WIDTH) {
    return "tablet";
  }
  return "phone";
}

/**
 * Effective column count `C` for a breakpoint. `desktop` returns the canvas's
 * authored `grid_columns` (passed in); `tablet` and `phone` return their fixed
 * tokens regardless of the authored count.
 */
export function effectiveColumns(
  kind: BreakpointKind,
  canvasGridColumns: number,
): number {
  switch (kind) {
    case "desktop":
      return canvasGridColumns;
    case "tablet":
      return TABLET_COLUMNS;
    case "phone":
      return PHONE_COLUMNS;
    default: {
      // Exhaustiveness guard: a new BreakpointKind must update this switch.
      const never: never = kind;
      return never;
    }
  }
}
